/* eslint-disable no-undef */
import { checkApiAuthorization } from '@zg-rentals/shields-js';
import { createRequestHeaders, requester as particlesRequester } from '@zg-rentals/particles-js-requester';
import queryUtils from './queryUtils';
import pick from 'lodash/pick';
import { logError } from '@zg-rentals/log-error';
import { getGlobalLogger } from '@zg-rentals/logger-base';

const logger = getGlobalLogger('utils/requester');

function createRequester(api, proxyPath) {
  return function requester({ method, endpoint, queryParams = {}, body = {}, preparse = true } = {}) {
    return async (dispatch, getState) => {
      const normalizedMethod = method.toLowerCase();
      const shouldIncludeBody = normalizedMethod !== 'get';

      const state = getState();
      const startTime = new Date().getTime();
      const traceId = state.app.traceId;

      const options = {
        method,
        url: endpoint,
        params: queryParams,
        headers: createRequestHeaders({
          method,
          cookies: state.app.originalServerSideRequestCookies,
          cookieObj: state.app.originalServerSideRequestCookies,
          userAgent: state.app.userAgent,
          ip: state.app.ip,
          brand: state.app.brand,
        }),
      };

      if (shouldIncludeBody) {
        options.data = body;
      }

      const requesterInstance = particlesRequester({ logger, api, proxyPath });

      logger?.warn({ options }, 'will send http request');

      return requesterInstance
        .request(options)
        .then((response = {}) =>
          checkApiAuthorization({
            res: response,
            brand: getState().app.brand,
            isInWebView: getState().app.isInWebView,
          }),
        )
        .then((response = {}) => {
          let { data: responseData = {}, status } = response;
          const endTime = new Date().getTime();

          logger?.warn(
            {
              url: endpoint + queryUtils.stringify(queryParams),
              status,
              data: responseData,
              traceId,
              duration: endTime - startTime,
            },
            'received http response',
          );

          // TODO: If API calls are made during SSR (beyond auth), response cookies need to be
          // captured and added to res.locals.apiResponseCookies. The auth provider handles this
          // for user auth, but other endpoints would need similar handling.
          // Current evidence suggests no other API calls happen during SSR, but this should be
          // verified if SSR API calls are added in the future.

          return preparse && responseData.response ? responseData.response : responseData;
        })
        .catch((err = {}) => {
          const errorContext = {
            brand: state?.app?.brand,
            method,
            traceId,
            endpoint,
            userAgent: state?.app?.userAgent,
          };

          if (err && err.request && err.response) {
            errorContext.responseInfo = pick(err.response, ['status', 'statusText', 'headers', 'data']);
            errorContext.requestInfo = pick(err.request, ['host', 'path', 'method', 'protocol', '_header']);
          }

          logError({
            error: err,
            errorType: 'HTTPError',
            context: errorContext,
          });

          return {
            success: false,
            error: err,
          };
        });
    };
  };
}

export const zillowRequester = createRequester(
  __SERVER__ ? process.env.ZG_API : window.location.origin,
  __DEV__ ? 'renter-hub/proxy/' : undefined,
);

// Note: hotpads-proxy necessary as API is called directly from non-hotpads domains in QA/prod
export const hotpadsRequester = createRequester(
  __SERVER__ ? process.env.HOTPADS_API : undefined,
  'renter-hub/hotpads-proxy/',
);
